/**
 * useLibraryPackageCell — LibraryPackageCell's state and writes: the editor
 * open/closed, the package awaiting its confirm, the instant apply on confirm
 * and the unassign on "No package". Split out of the cell (function limit).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type Dispatch, type SetStateAction } from 'react';
import type {
  FlowOptimizerLibraryProvider,
  FlowOptimizerPackage,
  FlowOptimizerPackageApplyResult,
} from '@trafficpulse/shared';

import { useLibraryPackages } from '@/features/flow-optimizer-library/api/hooks/useLibraryPackages';
import {
  useAssignPackage,
  useUnassignPackage,
  type AssignTarget,
} from '@/features/flow-optimizer-library/scheduler/useAssignPackage';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { NO_PACKAGE, type PackageEditorProps } from './PackageEditor';

export interface UseLibraryPackageCellParams {
  row: OptimizerRow;
  /** The row's tracker, narrowed by libraryProviderOf. */
  provider: FlowOptimizerLibraryProvider;
}

interface AssignParams {
  target: AssignTarget;
  campaignName: string;
  pending: FlowOptimizerPackage | null;
  setPending: Dispatch<SetStateAction<FlowOptimizerPackage | null>>;
  setEditing: Dispatch<SetStateAction<boolean>>;
}

function appliedCopy(result: FlowOptimizerPackageApplyResult, campaign: string): string {
  return result.killSwitch
    ? `"${result.packageName}" is saved — the kill switch is ON, nothing pushes until it's off.`
    : `"${result.packageName}" is live on ${campaign}.`;
}

/** Confirm → instant apply of the pending package; closes the editor on success. */
function useConfirmAssign(p: AssignParams) {
  const toast = useToast();
  const assign = useAssignPackage();
  const confirmAssign = () => {
    if (!p.pending) return;
    assign.mutate(
      { packageId: p.pending.id, target: p.target },
      {
        onSuccess: (result) => {
          p.setPending(null);
          p.setEditing(false);
          toast.success('Package applied', appliedCopy(result, p.campaignName));
        },
        onError: (err) => toast.error('Apply failed', getApiErrorMessage(err)),
      },
    );
  };
  return { confirmAssign, busy: assign.isPending };
}

/** "No package" → unassign; the saved config values are left as they are. */
function useUnassign(target: AssignTarget, setEditing: Dispatch<SetStateAction<boolean>>) {
  const toast = useToast();
  const unassign = useUnassignPackage();
  return () =>
    unassign.mutate(target, {
      onSuccess: () => {
        setEditing(false);
        toast.success('Package unassigned', 'The saved config keeps running unchanged.');
      },
      onError: (err) => toast.error('Unassign failed', getApiErrorMessage(err)),
    });
}

export function useLibraryPackageCell({ row, provider }: UseLibraryPackageCellParams) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<FlowOptimizerPackage | null>(null);
  const packages = useLibraryPackages(editing).data ?? [];
  const target = { provider, campaignId: row.campaignId, flowId: row.flowId };
  const assignParams = { target, campaignName: row.campaignName, pending, setPending, setEditing };
  const { confirmAssign, busy } = useConfirmAssign(assignParams);
  const unassign = useUnassign(target, setEditing);
  const onPick = (id: string) => {
    if (id === NO_PACKAGE) return unassign();
    setPending(packages.find((pkg) => pkg.id === id) ?? null);
  };
  const editor: Omit<PackageEditorProps, 'row'> = {
    packages,
    pending,
    busy,
    onPick,
    onConfirm: confirmAssign,
    onDismissPending: () => setPending(null),
    onClose: () => setEditing(false),
  };
  return { editing, open: () => setEditing(true), editor };
}
